// Kitchen flow Stage 3 — the single-item count form counts ONE place. With
// the flag on, the book it previews against must be that place's balance,
// not the item's tenant-wide total, or the backend's stale-preview guard
// answers 409 book_quantity_changed on every count (spec §3, "Per-location
// counts"). Flag off: inert — no queries, no picker, the form is unchanged.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { FlowLocation } from '@/lib/inventoryFlow/types'
import { CT } from '@/lib/inventoryFlow/countStrings'
import { cn } from '@/lib/utils'

export function useCountLocation(enabled: boolean, itemId: string) {
  const { data: locRes } = useQuery({ queryKey: ['inventory-flow', 'locations'], queryFn: () => apiClient.getFlowLocations(), enabled })
  const { data: itemsRes, isLoading } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), enabled, staleTime: 0 })
  const places = useMemo(() => (locRes?.data ?? []).filter((l: FlowLocation) => l.is_active && l.kind !== 'transit'), [locRes])
  const [chosen, setChosen] = useState<string | null>(null)
  const locationId = chosen ?? places.find((p) => p.is_receiving_default)?.id ?? places[0]?.id ?? null
  const item = itemsRes?.data?.find((i) => i.id === itemId)
  const book = enabled && locationId && item ? item.balances[locationId] ?? 0 : null

  const ui = enabled && places.length > 1 ? (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{CT.where}</p>
      <div className="flex flex-wrap gap-2">
        {places.map((p) => (
          <button key={p.id} type="button" onClick={() => setChosen(p.id)}
            className={cn('h-10 rounded-lg border px-4 text-sm font-semibold', locationId === p.id ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}>
            {p.name}
          </button>
        ))}
      </div>
    </div>
  ) : null

  return { ui, locationId, book, loading: enabled && (isLoading || book == null) }
}
